import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import { OrderInner } from "../Order/styled/OrderStyle";
import { AuthenticationContext } from "../../controls/auth/AuthenticationWrap";
import { UserRole } from "../../../constants/roles";
import {
	DebtBreadcrumb,
	BreadcrumbItem,
	BreadcrumbSeparator,
	DebtContainer,
	DebtSummaryCards,
	DebtCard,
	DebtCardIcon,
	DebtCardContent,
	DebtCardNumber,
	DebtCardText,
	DebtCardAmount,
} from "./styled/DebtManagementStyles";
import {
	getAdminPartnerPaymentRequests,
	AdminPartnerPaymentRequestRow,
} from "../../../services/PartnerPaymentRequestService";
import StationRequestListPanel from "./components/StationRequestListPanel";

function normalizeCurrencyCode(currencyCode: string | null | undefined): string | null {
	const s = `${currencyCode ?? ""}`.trim();
	return s ? s.toUpperCase() : null;
}

function getCurrencyLabel(currencyCode: string) {
	if (currencyCode === "VND") return "VNĐ";
	return currencyCode;
}

function formatPaymentAmount(value: number | null | undefined, currencyCode?: string | null) {
	const amount = Number(value);
	if (!Number.isFinite(amount)) return "-";
	const normalizedCurrencyCode = normalizeCurrencyCode(currencyCode);
	if (!normalizedCurrencyCode) {
		return amount.toLocaleString("en-US", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	}
	const fractionDigits = normalizedCurrencyCode === "VND" ? 0 : 2;
	const formatted = amount.toLocaleString("en-US", {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	});
	return `${formatted} ${getCurrencyLabel(normalizedCurrencyCode)}`;
}

function formatCurrencyBreakdown(rows: AdminPartnerPaymentRequestRow[]) {
	if (!rows.length) return "0";

	const totalsByCurrency = rows.reduce<Record<string, number>>((acc, row) => {
		const bucketKey = normalizeCurrencyCode(row.station?.currencyCode) ?? "_";
		acc[bucketKey] = (acc[bucketKey] || 0) + (Number(row.requestAmount) || 0);
		return acc;
	}, {});

	return Object.entries(totalsByCurrency)
		.sort(([currencyA], [currencyB]) => currencyA.localeCompare(currencyB))
		.map(([currencyCode, amount]) =>
			currencyCode === "_" ? formatPaymentAmount(amount, null) : formatPaymentAmount(amount, currencyCode)
		)
		.join(" | ");
}

export default function DebtManagement() {
	const authContext = useContext(AuthenticationContext);
	const { userInfo, isFinishedValidate } = authContext || {};
	const [hoveredCard, setHoveredCard] = useState<number | null>(null);
	const [paymentRequests, setPaymentRequests] = useState<AdminPartnerPaymentRequestRow[]>([]);

	const loadPaymentRequests = useCallback(async () => {
		try {
			const data = await getAdminPartnerPaymentRequests();
			setPaymentRequests(data || []);
		} catch {
			setPaymentRequests([]);
		}
	}, []);

	useEffect(() => {
		void loadPaymentRequests();
	}, [loadPaymentRequests]);

	if (isFinishedValidate && (!userInfo || userInfo.role !== UserRole.Admin)) {
		return (
			<MyBookingWrapper>
				<OrderInner>
					<div style={{ textAlign: "center", padding: "50px" }}>
						<h2>Access Denied</h2>
						<p>You don't have permission to access this page.</p>
					</div>
				</OrderInner>
			</MyBookingWrapper>
		);
	}

	const inTransitRequests = useMemo(
		() => paymentRequests.filter((r) => String(r.status).toUpperCase() === "IN_TRANSIT"),
		[paymentRequests]
	);

	const summaryData = [
		{
			title: "Awaiting payment",
			number: inTransitRequests.length,
			amount: formatCurrencyBreakdown(inTransitRequests),
			color: "#17B26A",
		},
	];

	return (
		<MyBookingWrapper>
			<OrderInner>
				<DebtBreadcrumb>
					<BreadcrumbItem
						as="a"
						href="/"
						style={{ textDecoration: "none" }}>
						{"Home"}
					</BreadcrumbItem>
					<BreadcrumbSeparator
						src="./static/img/direction-right.png"
						alt=">"
					/>
					<BreadcrumbItem active>{"Debt Management"}</BreadcrumbItem>
				</DebtBreadcrumb>
			</OrderInner>

			<DebtContainer>
				<DebtSummaryCards>
					{summaryData.map((item, index) => {
						const iconPath = "./static/img/debt/Icon4.png";
						const iconHoverPath = "./static/img/debt/Icon4-h.png";
						const currentIcon = hoveredCard === index ? iconHoverPath : iconPath;

						return (
							<DebtCard
								key={index}
								color={item.color}
								onMouseEnter={() => setHoveredCard(index)}
								onMouseLeave={() => setHoveredCard(null)}
								style={{ cursor: "default" }}>
								<DebtCardIcon color={item.color}>
									<img
										src={currentIcon}
										alt={item.title}
										style={{ width: "42px", height: "42px" }}
									/>
								</DebtCardIcon>
								<DebtCardContent>
									<DebtCardText>{item.title}</DebtCardText>
									<DebtCardNumber color={item.color}>{item.number}</DebtCardNumber>
									{item.amount && <DebtCardAmount color={item.color}>{item.amount}</DebtCardAmount>}
								</DebtCardContent>
							</DebtCard>
						);
					})}
				</DebtSummaryCards>

				<StationRequestListPanel
					onPaidSuccess={async () => {
						await loadPaymentRequests();
					}}
					partnerPaymentRequests={paymentRequests}
				/>
			</DebtContainer>
		</MyBookingWrapper>
	);
}
